import { H4 } from '@blueprintjs/core';
import * as React from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import { useAppSelector } from 'src/ui/app/hooks';
import { withConfirmationDs } from 'src/ui/components/withConfirmationDs';
import { noop } from 'src/ui/constants/empty';
import { BrowserAuthFailedWarning } from 'src/ui/containers/dashboard/BrowserAuthFailedWarning';
import { Dashboard } from 'src/ui/containers/dashboard/Dashboard';
import { DegradedWarning } from 'src/ui/containers/dashboard/DegradedWarning';
import { SSOWarning } from 'src/ui/containers/dashboard/SSOWarning';
import { UnauthorizedWarning } from 'src/ui/containers/dashboard/UnauthorizedWarning';
import { NewUserLanding } from 'src/ui/containers/landing/NewUserLanding';
import { Welcome } from 'src/ui/containers/landing/Welcome';
import { DEMO_CONNECTION_INDEX } from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useSavedConnectionsQuery } from 'src/ui/graph/types';
import {
  useCurrentConnection,
  useGetLastWorkspace,
  useSetLastConnectionIndex,
  useSetLastWorkspace,
} from 'src/ui/hooks/connection';
import {
  useCurrentConnectionIdx,
  useLandingConnectionIdx,
  useResolveWorkspaceLandingRoute,
} from 'src/ui/hooks/navbar/connection';
import { useListOrganizations } from 'src/ui/hooks/organization';
import { useQueryParams } from 'src/ui/hooks/query';
import {
  isBrowserAuthFailureSelector,
  isDegradedSelector,
  isUnauthorizedSelector,
  ssoErrorCodeSelector,
} from 'src/ui/selectors/connection';
import * as copy from 'src/ui/templates/copy';
import { buildRoute } from 'src/ui/utils/buildRoute';

export const LandingPage: React.VFC = () => {
  const isBrowserAuthFailure = useAppSelector(isBrowserAuthFailureSelector);
  const isDegraded = useAppSelector(isDegradedSelector);
  const isUnauthorized = useAppSelector(isUnauthorizedSelector);
  const ssoErrorCode = useAppSelector(ssoErrorCodeSelector);
  const history = useHistory();
  const { org = '' } = useParams<{ org?: string; id?: string }>();
  const queryParams = useQueryParams();

  const {
    data: connectionList,
    isLoading: loadingConnections,
    isFetching: fetchingConnections,
  } = useSavedConnectionsQuery(graphQLClient, org ? { org_domain: org } : {});
  const noConnections = !connectionList?.listConnections?.length;

  // The root `/` entry (no org, no connection id in the url) decides where to
  // land: the personal workspace renders inline (below), while an org member
  // with no personal endpoints is redirected to an org workspace.
  const urlConnectionIdx = useCurrentConnectionIdx();
  const isRoot = !org && !urlConnectionIdx;
  const {
    data: organizationsData,
    isLoading: loadingOrgs,
  } = useListOrganizations();
  const organizations = (
    organizationsData?.listOrganizations || []
  ).filter((o): o is NonNullable<typeof o> & { domain: string } =>
    Boolean(o?.domain)
  );
  const lastWorkspace = useGetLastWorkspace();
  const personalHasEndpoints = !noConnections;

  // An explicit personal choice (stored as '') keeps the user in the personal
  // workspace even when it is empty; without it, orgs are only needed when the
  // personal workspace has no endpoints (org vs new-user) or a stored org must
  // be validated. Otherwise the personal workspace renders inline immediately.
  const chosePersonal = lastWorkspace === '';
  const needsOrgs =
    isRoot &&
    !chosePersonal &&
    (!personalHasEndpoints || Boolean(lastWorkspace));

  let targetOrg: string | null = null;
  if (isRoot) {
    if (chosePersonal) {
      // explicitly last in personal — honor it, even with no endpoints
      targetOrg = '';
    } else if (
      lastWorkspace &&
      organizations.some((o) => o.domain === lastWorkspace)
    ) {
      // return to the last-used org, even when personal endpoints exist (AC #4)
      targetOrg = lastWorkspace;
    } else if (personalHasEndpoints) {
      targetOrg = '';
    } else if (organizations.length) {
      targetOrg = organizations[0].domain;
    }
  }
  const isOrgTarget = Boolean(isRoot && targetOrg);
  const {
    route: workspaceRoute,
    isLoading: loadingWorkspaceRoute,
  } = useResolveWorkspaceLandingRoute(targetOrg || '', isOrgTarget);

  const connectionIdx = useLandingConnectionIdx();
  const parsedIndex = parseInt(connectionIdx, 10);
  const currentIndex = Number.isNaN(parsedIndex) ? -1 : parsedIndex;
  const foundConnection =
    currentIndex !== DEMO_CONNECTION_INDEX && currentIndex !== -1
      ? connectionList?.listConnections?.find((c) => c?.index === currentIndex)
      : null;
  const {
    connection: demoConnection,
    isLoading: loadingDemoConnection,
  } = useCurrentConnection(
    currentIndex === DEMO_CONNECTION_INDEX
      ? DEMO_CONNECTION_INDEX.toString()
      : '-1',
    org
  );
  const currentConnection =
    currentIndex === DEMO_CONNECTION_INDEX ? demoConnection : foundConnection;
  const isMissingConnection = currentIndex !== -1 && !currentConnection;

  React.useEffect(() => {
    if (queryParams.has('upgradeSuccess')) {
      history.replace(history.location.pathname);
      withConfirmationDs({
        isCloseButtonShown: false,
        message: copy.components.welcomeToEssentialsDialog.body,
        noPrompt: null,
        onConfirmationSuccess: noop,
        title: copy.components.welcomeToEssentialsDialog.title,
        yesPrompt: copy.components.welcomeToEssentialsDialog.buttonText,
      });
    }
  }, [history, queryParams]);

  React.useEffect(() => {
    // wait for the connections query to settle (incl. background refetches, e.g.
    // right after a connection is created) before treating the url as stale.
    if (
      !loadingConnections &&
      !fetchingConnections &&
      !loadingDemoConnection &&
      isMissingConnection
    ) {
      history.push(buildRoute(org, ''));
    }
  }, [
    fetchingConnections,
    history,
    isMissingConnection,
    loadingConnections,
    loadingDemoConnection,
    org,
  ]);

  // set current page as last visited connection index
  useSetLastConnectionIndex(currentConnection?.index, org);
  // remember the workspace being viewed (personal or org) for the root landing,
  // but not on the root `/` entry itself (no workspace has been chosen yet), nor
  // from the demo connection — it lives in the personal workspace, so recording
  // it would pin an org member to their empty personal workspace on next login.
  useSetLastWorkspace(org, !isRoot && currentIndex !== DEMO_CONNECTION_INDEX);

  if (
    loadingConnections ||
    loadingDemoConnection ||
    isMissingConnection ||
    (needsOrgs && loadingOrgs) ||
    (isOrgTarget && loadingWorkspaceRoute)
  ) {
    return null;
  }

  if (isOrgTarget) {
    return <Redirect to={workspaceRoute} />;
  }

  if (noConnections) {
    return <NewUserLanding />;
  }

  return (
    <>
      <Welcome />
      {currentConnection ? (
        <div>
          {isBrowserAuthFailure ? (
            <BrowserAuthFailedWarning index={currentIndex} org={org} />
          ) : null}
          {isDegraded ? (
            <DegradedWarning index={currentIndex} org={org} />
          ) : null}
          {ssoErrorCode ? <SSOWarning errorCode={ssoErrorCode} /> : null}
          {isUnauthorized ? (
            <UnauthorizedWarning index={currentIndex} org={org} />
          ) : null}
          <div className="sd-landing-dashboard">
            <H4 className="sd-landing-header">{currentConnection.name}</H4>
            <Dashboard index={currentIndex} />
          </div>
        </div>
      ) : null}
    </>
  );
};
